import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Btn from './ui/Btn';
import GDriveSyncButton from './GDriveSyncButton';
import PushToDriveButton from './PushToDriveButton';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import { useAuthStore } from '../stores/auth';
import { PieceClient, PieceClientStatutDepot } from '../lib/types';

/**
 * Onglet « Pièces client » du hub dossier (M3 / Sprint 6 / ADR-049).
 *
 * Mode métadonnée : titre, description, date, statut de dépôt,
 * référence dépôt. Sprint 6 (3) introduira l'upload de fichier
 * (jumelle de pdfs) avec le flag ``has_file``.
 */

const STATUT_LABELS: Record<PieceClientStatutDepot, string> = {
  non_deposee: 'Non déposée',
  a_deposer: 'À déposer',
  deposee: 'Déposée',
  communiquee: 'Communiquée',
  retiree: 'Retirée',
  rejetee: 'Rejetée',
};

const STATUT_BADGE: Record<PieceClientStatutDepot, string> = {
  non_deposee: 'bg-paper-subtle text-text-secondary',
  a_deposer: 'bg-cat-imp-pastel text-cat-imp-text',
  deposee: 'bg-cat-fav-pastel text-cat-fav',
  communiquee: 'bg-accent-light text-accent-text',
  retiree: 'bg-paper-subtle text-text-tertiary line-through',
  rejetee: 'bg-cat-def-pastel text-cat-def',
};

const STATUT_ORDER: PieceClientStatutDepot[] = [
  'a_deposer',
  'non_deposee',
  'deposee',
  'communiquee',
  'rejetee',
  'retiree',
];

type DraftForm = {
  titre: string;
  description: string;
  date_piece: string;
  statut_depot: PieceClientStatutDepot;
  date_depot: string;
  reference_depot: string;
};

const EMPTY_DRAFT: DraftForm = {
  titre: '',
  description: '',
  date_piece: '',
  statut_depot: 'non_deposee',
  date_depot: '',
  reference_depot: '',
};

type RowProps = {
  piece: PieceClient;
  dossierId: string;
  onEdit: () => void;
  onDelete: () => void;
  onStatutChange: (s: PieceClientStatutDepot) => void;
  onUploadFile: (file: File) => void;
  onOpenFile: () => void;
  onReconvert: () => void;
};

type Props = {
  dossierId: string;
  onChange?: () => void;
};

export default function PiecesClientTab({ dossierId, onChange }: Props) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [pieces, setPieces] = useState<PieceClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftForm>(EMPTY_DRAFT);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.get<PieceClient[]>(`/dossiers/${dossierId}/pieces-client`);
      setPieces(rows);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const buildPayload = (d: DraftForm): Record<string, unknown> => {
    const out: Record<string, unknown> = {
      titre: d.titre.trim(),
      statut_depot: d.statut_depot,
    };
    if (d.description.trim()) out.description = d.description.trim();
    if (d.date_piece) out.date_piece = d.date_piece;
    if (d.date_depot) out.date_depot = d.date_depot;
    if (d.reference_depot.trim()) out.reference_depot = d.reference_depot.trim();
    return out;
  };

  const submitNew = async () => {
    if (!draft.titre.trim()) return;
    setSubmitting(true);
    try {
      await api.post<PieceClient>(`/dossiers/${dossierId}/pieces-client`, buildPayload(draft));
      setAdding(false);
      setDraft(EMPTY_DRAFT);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (p: PieceClient) => {
    setEditingId(p.id);
    setEditDraft({
      titre: p.titre,
      description: p.description ?? '',
      date_piece: p.date_piece ?? '',
      statut_depot: p.statut_depot,
      date_depot: p.date_depot ?? '',
      reference_depot: p.reference_depot ?? '',
    });
  };

  const submitEdit = async () => {
    if (!editingId || !editDraft.titre.trim()) return;
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        titre: editDraft.titre.trim(),
        description: editDraft.description.trim() || null,
        date_piece: editDraft.date_piece || null,
        statut_depot: editDraft.statut_depot,
        date_depot: editDraft.date_depot || null,
        reference_depot: editDraft.reference_depot.trim() || null,
      };
      await api.patch<PieceClient>(`/pieces-client/${editingId}`, body);
      setEditingId(null);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (p: PieceClient) => {
    if (!window.confirm(`Supprimer « ${p.titre} » ?`)) return;
    try {
      await api.delete(`/pieces-client/${p.id}`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const updateStatut = async (p: PieceClient, statut: PieceClientStatutDepot) => {
    try {
      await api.patch<PieceClient>(`/pieces-client/${p.id}`, { statut_depot: statut });
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const uploadFile = async (p: PieceClient, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.postForm<PieceClient>(`/pieces-client/${p.id}/upload`, fd);
      toast.success(`« ${p.titre} » : fichier joint.`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const reconvertFile = async (p: PieceClient) => {
    try {
      await api.post<PieceClient>(`/pieces-client/${p.id}/reconvert`, {});
      toast.success(`Reconversion lancée pour « ${p.titre} »`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const openFile = async (p: PieceClient) => {
    try {
      const resp = await fetch(`/api/v1/pieces-client/${p.id}/stream`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // URL stays valid for the new tab; revoke after a delay so the browser
      // has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error((err as Error).message ?? 'Erreur réseau');
    }
  };

  // Groupé par statut pour visibilité — l'avocat regarde « ce qu'il
  // reste à déposer » d'un coup d'œil.
  const byStatut = new Map<PieceClientStatutDepot, PieceClient[]>();
  for (const p of pieces) {
    if (!byStatut.has(p.statut_depot)) byStatut.set(p.statut_depot, []);
    byStatut.get(p.statut_depot)!.push(p);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-paper-elevated p-3">
        <div className="text-[12px] text-text-secondary">
          <strong className="font-medium text-text-primary">{pieces.length}</strong> pièce
          {pieces.length > 1 ? 's' : ''} client
        </div>
        <div className="flex items-center gap-2">
          <GDriveSyncButton dossierId={dossierId} onImported={() => void load()} />
          {!adding && (
            <Btn
              variant="primary"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_DRAFT);
                setAdding(true);
              }}
            >
              + Ajouter une pièce client
            </Btn>
          )}
        </div>
      </div>

      {adding && (
        <PieceForm
          draft={draft}
          setDraft={setDraft}
          onCancel={() => {
            setAdding(false);
            setDraft(EMPTY_DRAFT);
          }}
          onSubmit={() => void submitNew()}
          submitting={submitting}
          submitLabel="Créer la pièce"
        />
      )}

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-text-secondary">Chargement…</p>
      ) : pieces.length === 0 && !adding ? (
        <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucune pièce client. Ajoute les documents transmis par le client (mails, contrats,
          témoignages, attestations…) — ce tab est distinct de l'inventaire greffe.
        </p>
      ) : (
        <div className="space-y-5">
          {STATUT_ORDER.filter((s) => byStatut.has(s)).map((s) => {
            const rows = byStatut.get(s)!;
            return (
              <section key={s} className="space-y-2">
                <header className="flex items-baseline justify-between border-b border-rule-strong pb-1">
                  <h3 className="font-serif text-[15px] font-medium text-text-primary">
                    {STATUT_LABELS[s]} <span className="text-text-tertiary">· {rows.length}</span>
                  </h3>
                </header>
                <ul className="space-y-2">
                  {rows.map((p) =>
                    editingId === p.id ? (
                      <li key={p.id}>
                        <PieceForm
                          draft={editDraft}
                          setDraft={setEditDraft}
                          onCancel={() => setEditingId(null)}
                          onSubmit={() => void submitEdit()}
                          submitting={submitting}
                          submitLabel="Enregistrer"
                        />
                      </li>
                    ) : (
                      <li key={p.id}>
                        <PieceRow
                          piece={p}
                          dossierId={dossierId}
                          onEdit={() => startEdit(p)}
                          onDelete={() => void remove(p)}
                          onStatutChange={(s) => void updateStatut(p, s)}
                          onUploadFile={(f) => void uploadFile(p, f)}
                          onOpenFile={() => void openFile(p)}
                          onReconvert={() => void reconvertFile(p)}
                        />
                      </li>
                    )
                  )}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PieceRow({
  piece: p,
  dossierId,
  onEdit,
  onDelete,
  onStatutChange,
  onUploadFile,
  onOpenFile,
  onReconvert,
}: RowProps) {
  const conversionFailed =
    p.conversion_status === 'failed' || p.conversion_status === 'unsupported';
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="group flex items-start justify-between gap-3 rounded-sm border border-rule bg-paper-elevated p-3.5 hover:bg-paper-card">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-serif text-[15px] font-medium text-text-primary">{p.titre}</span>
          <select
            value={p.statut_depot}
            onChange={(e) => onStatutChange(e.target.value as PieceClientStatutDepot)}
            className={`appearance-none cursor-pointer rounded-pill px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] focus:outline-none ${STATUT_BADGE[p.statut_depot]}`}
            title="Changer le statut de dépôt"
          >
            {(Object.keys(STATUT_LABELS) as PieceClientStatutDepot[]).map((s) => (
              <option key={s} value={s}>
                {STATUT_LABELS[s]}
              </option>
            ))}
          </select>
          {p.has_file ? (
            <>
              <button
                onClick={onOpenFile}
                className="inline-flex items-center rounded-pill bg-cat-fav-pastel px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-cat-fav hover:underline"
                title="Ouvrir le fichier dans un nouvel onglet"
              >
                fichier joint
                {p.page_count != null && (
                  <span className="ml-1 font-mono">· {p.page_count} p.</span>
                )}
              </button>
              <Link
                to={`/dossiers/${dossierId}/pieces-client/${p.id}`}
                className="inline-flex items-center rounded-pill bg-accent-light px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-accent-text hover:underline no-underline"
                title="Annoter cette pièce client (ADR-060)"
              >
                annoter →
              </Link>
              {conversionFailed && (
                <button
                  onClick={onReconvert}
                  className="inline-flex items-center rounded-pill bg-cat-imp-pastel px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-cat-imp-text hover:bg-cat-imp/20"
                  title={p.conversion_error || 'Relancer la conversion + OCR'}
                >
                  reconvertir
                </button>
              )}
            </>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center rounded-pill border border-dashed border-rule-strong bg-paper-elevated px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] text-text-tertiary hover:border-accent hover:text-accent-text"
              title="Joindre un fichier (PDF, DOCX, image…)"
            >
              + joindre
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                onUploadFile(f);
                e.target.value = '';
              }
            }}
          />
        </div>
        {conversionFailed && (
          <p
            className="mt-1 rounded bg-cat-def-pastel/40 px-2 py-1 text-[11.5px] text-cat-def"
            title={p.conversion_error || undefined}
          >
            <strong>
              Conversion {p.conversion_status === 'unsupported' ? 'non supportée' : 'échouée'} :
            </strong>{' '}
            {p.conversion_error || 'erreur inconnue'}
          </p>
        )}
        {p.description && (
          <p className="mt-1 whitespace-pre-wrap text-[13px] text-text-secondary">
            {p.description}
          </p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
          {p.date_piece && (
            <span>
              <span className="text-text-secondary">Date pièce :</span>{' '}
              <span className="font-mono">{p.date_piece}</span>
            </span>
          )}
          {p.date_depot && (
            <span>
              <span className="text-text-secondary">Dépôt :</span>{' '}
              <span className="font-mono">{p.date_depot}</span>
            </span>
          )}
          {p.reference_depot && (
            <span>
              <span className="text-text-secondary">Réf :</span>{' '}
              <span className="font-mono">{p.reference_depot}</span>
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
        {p.has_file && (
          <PushToDriveButton
            dossierId={dossierId}
            kind="piece_client"
            pieceId={p.id}
            pieceTitle={p.titre}
            variant="icon"
          />
        )}
        <button
          onClick={onEdit}
          className="rounded p-1 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
          title="Modifier"
        >
          ✎
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
          title="Supprimer"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function PieceForm({
  draft,
  setDraft,
  onCancel,
  onSubmit,
  submitting,
  submitLabel,
}: {
  draft: DraftForm;
  setDraft: (d: DraftForm) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  const labelCls = 'block text-xs font-medium uppercase tracking-[0.3px] text-text-secondary';
  const inputCls =
    'mt-1 w-full rounded-sm border border-rule-strong bg-paper-elevated px-2.5 py-1.5 text-sm font-normal normal-case tracking-normal text-text-primary';
  return (
    <div className="space-y-3 rounded-md border border-accent/40 bg-accent-lighter p-4">
      <label className={labelCls}>
        Titre *
        <input
          value={draft.titre}
          onChange={(e) => setDraft({ ...draft, titre: e.target.value })}
          autoFocus
          required
          placeholder="ex. Mail du client du 15/03/2024"
          className={inputCls}
        />
      </label>
      <label className={labelCls}>
        Description
        <textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          rows={2}
          placeholder="Contexte, contenu, justification du dépôt…"
          className={inputCls}
        />
      </label>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className={labelCls}>
          Date de la pièce
          <input
            type="date"
            value={draft.date_piece}
            onChange={(e) => setDraft({ ...draft, date_piece: e.target.value })}
            className={inputCls}
          />
        </label>
        <label className={labelCls}>
          Statut de dépôt
          <select
            value={draft.statut_depot}
            onChange={(e) =>
              setDraft({ ...draft, statut_depot: e.target.value as PieceClientStatutDepot })
            }
            className={inputCls}
          >
            {(Object.keys(STATUT_LABELS) as PieceClientStatutDepot[]).map((s) => (
              <option key={s} value={s}>
                {STATUT_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className={labelCls}>
          Date de dépôt
          <input
            type="date"
            value={draft.date_depot}
            onChange={(e) => setDraft({ ...draft, date_depot: e.target.value })}
            className={inputCls}
          />
        </label>
      </div>
      <label className={labelCls}>
        Référence de dépôt
        <input
          value={draft.reference_depot}
          onChange={(e) => setDraft({ ...draft, reference_depot: e.target.value })}
          placeholder="ex. Bordereau pièces #12 / numéro de plumitif"
          className={inputCls}
        />
      </label>
      <div className="flex justify-end gap-1.5">
        <Btn variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Annuler
        </Btn>
        <Btn
          variant="primary"
          size="sm"
          onClick={onSubmit}
          disabled={submitting || !draft.titre.trim()}
        >
          {submitting ? '…' : submitLabel}
        </Btn>
      </div>
    </div>
  );
}
